import type { Term } from './EventBus';

const STORAGE_KEY = 'ar-rush:save-v1';

export interface PersistedUnlock {
  term: Term;
  fictionalMinutes: number;
  beatId: string | null;
}

export interface SaveData {
  /** Set of terms ever unlocked across all runs. */
  unlockedTerms: readonly Term[];
  /** First-seen unlock record per term (kept from the run that first unlocked it). */
  firstUnlocks: Record<string, PersistedUnlock>;
  /** Best craft score across all runs (sum of scorecard fields used as proxy). */
  bestCraftScore: number;
  /** Number of full days completed (reaching the EndingScene). */
  daysCompleted: number;
  /** Best DSO ever (lower is better). null if no run has ended yet. */
  bestDso: number | null;
}

const EMPTY: SaveData = {
  unlockedTerms: [],
  firstUnlocks: {},
  bestCraftScore: 0,
  daysCompleted: 0,
  bestDso: null,
};

const ALL_TERMS: ReadonlySet<Term> = new Set<Term>([
  'aging-bucket',
  'dunning',
  'cash-application',
  'short-pay',
  'dispute',
  'promise-to-pay',
  'write-off',
  'dso',
]);

const isPersistedUnlock = (v: unknown): v is PersistedUnlock => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.term === 'string' &&
    typeof o.fictionalMinutes === 'number' &&
    (o.beatId === null || typeof o.beatId === 'string') &&
    ALL_TERMS.has(o.term as Term)
  );
};

const validate = (v: unknown): SaveData | null => {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (!Array.isArray(o.unlockedTerms)) return null;
  if (!o.firstUnlocks || typeof o.firstUnlocks !== 'object') return null;
  if (typeof o.bestCraftScore !== 'number') return null;
  if (typeof o.daysCompleted !== 'number') return null;
  if (o.bestDso !== null && typeof o.bestDso !== 'number') return null;

  const unlockedTerms = (o.unlockedTerms as unknown[]).filter(
    (t): t is Term => typeof t === 'string' && ALL_TERMS.has(t as Term),
  );
  const firstUnlocks: Record<string, PersistedUnlock> = {};
  for (const [k, v] of Object.entries(o.firstUnlocks as Record<string, unknown>)) {
    if (isPersistedUnlock(v)) firstUnlocks[k] = v;
  }
  return {
    unlockedTerms,
    firstUnlocks,
    bestCraftScore: o.bestCraftScore,
    daysCompleted: o.daysCompleted,
    bestDso: (o.bestDso as number | null) ?? null,
  };
};

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const memoryAdapter = (): StorageAdapter => {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => { map.set(k, v); },
    removeItem: (k) => { map.delete(k); },
  };
};

export class SaveStore {
  private cached: SaveData;
  private readonly storage: StorageAdapter;

  constructor(storage?: StorageAdapter) {
    this.storage = storage ?? (typeof localStorage !== 'undefined' ? localStorage : memoryAdapter());
    this.cached = this.load();
  }

  private load(): SaveData {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return { ...EMPTY };
      const parsed = JSON.parse(raw);
      const validated = validate(parsed);
      return validated ?? { ...EMPTY };
    } catch {
      return { ...EMPTY };
    }
  }

  snapshot(): Readonly<SaveData> {
    return {
      ...this.cached,
      unlockedTerms: [...this.cached.unlockedTerms],
      firstUnlocks: { ...this.cached.firstUnlocks },
    };
  }

  recordUnlock(term: Term, fictionalMinutes: number, beatId: string | null): void {
    if (this.cached.unlockedTerms.includes(term)) return;
    this.cached = {
      ...this.cached,
      unlockedTerms: [...this.cached.unlockedTerms, term],
      firstUnlocks: {
        ...this.cached.firstUnlocks,
        [term]: { term, fictionalMinutes, beatId },
      },
    };
    this.persist();
  }

  recordRunComplete(craftScore: number, dso: number): void {
    this.cached = {
      ...this.cached,
      daysCompleted: this.cached.daysCompleted + 1,
      bestCraftScore: Math.max(this.cached.bestCraftScore, craftScore),
      bestDso: this.cached.bestDso === null ? dso : Math.min(this.cached.bestDso, dso),
    };
    this.persist();
  }

  hasCompletedFirstDay(): boolean {
    return this.cached.daysCompleted >= 1;
  }

  reset(): void {
    this.cached = { ...EMPTY };
    this.storage.removeItem(STORAGE_KEY);
  }

  private persist(): void {
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.cached));
    } catch {
      // localStorage may throw in private browsing; silently degrade.
    }
  }
}

export const memoryStorageAdapter = memoryAdapter;
